/**
 * Patrol summary + review, shown once a run ends — the patrol counterpart to the
 * dispatch flow's CompletePhase. `outcome` distinguishes the three ways a run
 * can end: a halted or failed patrol is still worth reviewing, but calling
 * either "completed" would misreport what the robot actually did.
 */
import { useState } from "react";
import PropTypes from "prop-types";
import { Box, Stack, Typography, Button, TextField, CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import RouteIcon from "@mui/icons-material/Route";
import ReplayIcon from "@mui/icons-material/Replay";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { tokens } from "../../theme";

// One entry per terminal state the robot's patrol_runner.py can report.
// `error` is deliberately NOT styled as a success: an operator must be able to
// tell at a glance that the route was not actually covered.
const OUTCOMES = {
  completed: {
    Icon: CheckCircleIcon,
    title: "PATROL COMPLETE",
    sub: "The robot finished every round on its patrol route.",
    tone: tokens.success,
    toneSoft: tokens.successSoft,
  },
  stopped: {
    Icon: StopCircleIcon,
    title: "PATROL STOPPED",
    sub: "The patrol was halted before finishing all rounds.",
    tone: tokens.warning,
    toneSoft: tokens.warningSoft,
  },
  error: {
    Icon: ErrorOutlineIcon,
    title: "PATROL FAILED",
    sub: "The robot reported an error and stopped patrolling.",
    tone: tokens.danger,
    toneSoft: tokens.dangerSoft,
  },
};

const StatTile = ({ value, label }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      background: tokens.surface2,
      border: `1px solid ${tokens.border}`,
      borderRadius: "10px",
      py: 1.5,
      px: 1,
      textAlign: "center",
    }}
  >
    <Typography sx={{ fontSize: 20, fontWeight: 700, color: tokens.text, lineHeight: 1.2 }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: 11, color: tokens.text3, mt: 0.25 }}>{label}</Typography>
  </Box>
);

StatTile.propTypes = { value: PropTypes.node, label: PropTypes.string };

const PatrolCompletePhase = ({
  outcome = "completed",
  patrolState,
  waypoints = [],
  rounds,
  onRate,
  onNewPatrol,
  onDone,
  crumbs,
}) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const parentCrumb = crumbs?.length > 1 ? crumbs.at(-2) : null;
  const doneLabel = parentCrumb ? `Back to ${parentCrumb.label}` : "Back to Robots";

  const meta = OUTCOMES[outcome] || OUTCOMES.completed;
  const { Icon: OutcomeIcon } = meta;

  const roundsCompleted = Number(patrolState?.current_round) || 0;
  const totalRounds = Number(rounds) || 0;
  // Named waypoints only — a half-filled row in the setup form is not a stop.
  const namedWaypoints = waypoints.filter((w) => w?.name);

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    // Only lock the card once the review actually saved: on failure the form
    // stays open so the rating is not silently lost.
    const ok = await onRate?.(rating, comment);
    setSubmitting(false);
    if (ok) setSubmitted(true);
    else setSubmitError("Could not save your rating. Please try again.");
  };

  let ratingHint = "Tap a star to rate";
  if (submitted) ratingHint = "Rating submitted ✓";
  else if (submitting) ratingHint = "Submitting…";
  else if (rating > 0) ratingHint = `${rating} star${rating > 1 ? "s" : ""} selected`;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        // Capped so the summary does not stretch into an unreadable band on a
        // wide monitor; `mx: auto` keeps the capped block centred.
        width: "100%",
        maxWidth: 560,
        mx: "auto",
        pb: 1,
      }}
    >
      {/* Outcome banner */}
      <Box
        sx={{
          background: meta.toneSoft,
          border: `1px solid ${meta.tone}`,
          borderRadius: "14px",
          p: { xs: 2.5, sm: 3 },
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: tokens.surface,
            display: "grid",
            placeItems: "center",
            mx: "auto",
            mb: 1.5,
          }}
        >
          <OutcomeIcon sx={{ fontSize: 38, color: meta.tone }} />
        </Box>
        <Typography
          sx={{ fontSize: 18, fontWeight: 800, letterSpacing: "0.4px", color: meta.tone }}
        >
          {meta.title}
        </Typography>
        <Typography sx={{ fontSize: 13, color: tokens.text2, mt: 0.5, lineHeight: 1.5 }}>
          {meta.sub}
        </Typography>
      </Box>

      {/* Run summary — what the patrol actually covered */}
      <Box
        sx={{
          background: tokens.surface,
          border: `1px solid ${tokens.border}`,
          borderRadius: "14px",
          p: 2,
        }}
      >
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
          <RouteIcon sx={{ fontSize: 17, color: tokens.accent }} />
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: tokens.text }}>
            Patrol Summary
          </Typography>
        </Stack>

        <Stack direction="row" gap={1.5}>
          <StatTile
            value={
              totalRounds > 0 ? `${Math.min(roundsCompleted, totalRounds)}/${totalRounds}` : "—"
            }
            label="Rounds"
          />
          <StatTile value={namedWaypoints.length} label="Waypoints" />
        </Stack>

        {namedWaypoints.length > 0 && (
          <Stack gap={0.75} sx={{ mt: 1.5 }}>
            {namedWaypoints.map((w, i) => (
              <Stack key={w.id ?? w.name} direction="row" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    flex: "none",
                    background: tokens.accentSoft,
                    color: tokens.accent,
                    display: "grid",
                    placeItems: "center",
                    fontSize: 10.5,
                    fontWeight: 700,
                  }}
                >
                  {i + 1}
                </Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: tokens.text,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {w.name}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>

      {/* Exits — kept above the rating so they are visible without scrolling */}
      <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
        <Button
          fullWidth
          variant="contained"
          onClick={onNewPatrol}
          startIcon={<ReplayIcon sx={{ fontSize: "17px !important" }} />}
          sx={{
            background: tokens.accent,
            "&:hover": { background: tokens.accent2 },
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Continue Patrolling
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={onDone}
          startIcon={<ArrowBackIcon sx={{ fontSize: "17px !important" }} />}
          sx={{
            borderColor: tokens.border,
            color: tokens.text,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {doneLabel}
        </Button>
      </Stack>

      {/* Rating — same treatment as the delivery flow's, so a review reads the
          same way regardless of which mode the run was. */}
      <Box
        sx={{
          background: tokens.surface,
          border: `1px solid ${tokens.border}`,
          borderRadius: "14px",
          p: 2,
        }}
      >
        <Typography
          sx={{ fontSize: 13.5, fontWeight: 700, color: tokens.text, mb: 1, textAlign: "center" }}
        >
          Rate This Patrol
        </Typography>

        <Stack
          direction="row"
          justifyContent="center"
          gap={0.5}
          onMouseLeave={() => setHoverRating(0)}
        >
          {[1, 2, 3, 4, 5].map((s) => {
            const filled = s <= (hoverRating || rating);
            const StarCmp = filled ? StarIcon : StarBorderIcon;
            const locked = submitted || submitting;
            return (
              <Box
                component="button"
                type="button"
                key={s}
                aria-label={`Rate ${s} star${s > 1 ? "s" : ""}`}
                disabled={locked}
                onClick={() => setRating(s)}
                onMouseEnter={() => !locked && setHoverRating(s)}
                sx={{
                  border: "none",
                  background: "none",
                  p: 0.25,
                  lineHeight: 0,
                  cursor: locked ? "default" : "pointer",
                  transition: "transform .12s ease",
                  "&:hover": { transform: locked ? "none" : "scale(1.14)" },
                  "&:focus-visible": {
                    outline: `2px solid ${tokens.accent}`,
                    outlineOffset: 2,
                    borderRadius: "6px",
                  },
                }}
              >
                <StarCmp sx={{ fontSize: 30, color: filled ? tokens.warning : tokens.text3 }} />
              </Box>
            );
          })}
        </Stack>

        <Typography
          sx={{
            fontSize: 11.5,
            textAlign: "center",
            mt: 0.5,
            color: submitted ? tokens.success : tokens.text3,
          }}
        >
          {ratingHint}
        </Typography>

        {!submitted && rating > 0 && (
          <Stack gap={1} sx={{ mt: 1.5 }}>
            <TextField
              size="small"
              fullWidth
              multiline
              maxRows={3}
              placeholder="Add a comment (optional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={submitting}
              sx={{ "& .MuiOutlinedInput-root": { fontSize: 13 } }}
            />
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={
                submitting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : undefined
              }
              sx={{
                background: tokens.success,
                "&:hover": { background: tokens.success },
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {submitting ? "Submitting…" : "Submit Rating"}
            </Button>
            {submitError && (
              <Typography sx={{ fontSize: 12, color: tokens.danger, textAlign: "center" }}>
                {submitError}
              </Typography>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

PatrolCompletePhase.propTypes = {
  outcome: PropTypes.oneOf(["completed", "stopped", "error"]),
  patrolState: PropTypes.object,
  waypoints: PropTypes.array,
  rounds: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onRate: PropTypes.func,
  onNewPatrol: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  crumbs: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string.isRequired, route: PropTypes.string })
  ),
};

export default PatrolCompletePhase;
